import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage, ChatMilestone } from '../../models/chat';
import { listProjectAssets } from '../../services/projectAssets';
import { ASSET_REFERENCE_BG, ASSET_REFERENCE_COLOR, splitAssetMentions } from './assetMentions';
import { chatTimeline } from './jobSteps';
import { IconCheck, IconChevronD, IconChevronR, IconInfo, IconX } from '../Icons';
import RelativeTime from '../RelativeTime';

/** How close to the bottom, in pixels, still counts as reading the newest message. */
const BottomSlackPx = 24;

interface ChatMessageListProps {
  messages: ChatMessage[];
  /** Every step the chat's jobs reported; they are shown apart from the messages. */
  milestones: ChatMilestone[];
  isLoading: boolean;
  isDraftMode: boolean;
  /** The chat's job while it runs, whose steps are shown live. */
  runningJobId: string | null;
  /** The project's values are being arranged into panels, so a request of this chat waits for it. */
  organizing: boolean;
  onCancel: () => void;
  isCancelling: boolean;
  userName?: string;
  projectId?: string;
}

export default function ChatMessageList({
  messages,
  milestones,
  isLoading,
  isDraftMode,
  runningJobId,
  organizing,
  onCancel,
  isCancelling,
  userName = 'You',
  projectId,
}: ChatMessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const followsBottomRef = useRef(true);
  const [assetNames, setAssetNames] = useState<string[]>([]);
  const isCurrentRunning = runningJobId !== null;
  const timeline = useMemo(
    () => chatTimeline(messages, milestones, runningJobId),
    [messages, milestones, runningJobId],
  );

  useEffect(() => {
    followsBottomRef.current = true;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, milestones, isCurrentRunning]);

  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (followsBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(el);
    observer.observe(content);
    return () => observer.disconnect();
  }, [isLoading, isDraftMode]);

  const followBottomIfThere = () => {
    const el = scrollRef.current;
    if (el) followsBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= BottomSlackPx;
  };

  const stopFollowingBottom = () => {
    followsBottomRef.current = false;
  };

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    listProjectAssets(projectId)
      .then((assets) => {
        if (!cancelled) setAssetNames(assets.map((a) => a.name));
      })
      .catch(() => {
        if (!cancelled) setAssetNames([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center' }} className="muted">
        Loading messages…
      </div>
    );
  }

  if (isDraftMode) {
    return (
      <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
        <div>
          <div className="display" style={{ fontSize: 18, marginBottom: 6 }}>New chat</div>
          <div className="muted" style={{ fontSize: 13 }}>Type a message below to begin.</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="scroll-thin"
      onScroll={followBottomIfThere}
      style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
    >
      <div
        ref={contentRef}
        onPointerDown={stopFollowingBottom}
        onKeyDown={stopFollowingBottom}
        style={{ padding: '8px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        {messages.length === 0 && !isCurrentRunning && (
          <div className="muted" style={{ textAlign: 'center', fontSize: 13, paddingTop: 24 }}>
            No messages yet.
          </div>
        )}
        {timeline.items.map((item) =>
          item.kind === 'steps' ? (
            <LeftoverStepsRow key={`steps-${item.jobId}`} steps={item.steps} />
          ) : item.message.role === 'System' ? (
            <SystemMessageRow key={item.message.id} message={item.message} />
          ) : (
            <MessageRow
              key={item.message.id}
              message={item.message}
              steps={item.steps}
              userName={userName}
              assetNames={assetNames}
            />
          ),
        )}
        {isCurrentRunning && (
          <PlaceholderRow
            steps={timeline.running}
            waitingForOrganize={organizing}
            onCancel={onCancel}
            isCancelling={isCancelling}
          />
        )}
      </div>
    </div>
  );
}

function Avatar({ role }: { role: 'user' | 'asst' }) {
  return (
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        flex: 'none',
        background: role === 'user' ? '#3a3a44' : 'var(--accent)',
        color: role === 'user' ? '#fff' : 'var(--accent-ink)',
        display: 'grid',
        placeItems: 'center',
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        fontSize: 11,
      }}
    >
      {role === 'user' ? 'U' : 'E'}
    </div>
  );
}

function MessageContent({ content, assetNames }: { content: string; assetNames: string[] }) {
  const segments = splitAssetMentions(content, assetNames);
  return (
    <>
      {segments.map((seg, i) =>
        seg.isReference ? (
          <span
            key={i}
            style={{
              color: ASSET_REFERENCE_COLOR,
              background: ASSET_REFERENCE_BG,
              borderRadius: 4,
              padding: '0 3px',
            }}
          >
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}

function MessageRow({
  message,
  steps,
  userName,
  assetNames,
}: {
  message: ChatMessage;
  steps: ChatMilestone[];
  userName: string;
  assetNames: string[];
}) {
  const isUser = message.role === 'User';
  const failed = !isUser && message.state === 'Failed';
  const cancelled = !isUser && message.state === 'Cancelled';
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <Avatar role={isUser ? 'user' : 'asst'} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{isUser ? userName : 'Entire'}</div>
          <RelativeTime className="mono muted" style={{ fontSize: 11 }} value={message.createdAt} />
          {failed && <span className="chip warn"><span className="dot" />Failed</span>}
          {cancelled && <span className="chip"><span className="dot" />Cancelled</span>}
        </div>
        {steps.length > 0 && <StepsToggle steps={steps} />}
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: cancelled ? 'var(--text-5)' : 'var(--text)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontStyle: cancelled ? 'italic' : 'normal',
          }}
        >
          {isUser ? <MessageContent content={message.content} assetNames={assetNames} /> : message.content}
        </div>
        {message.images && message.images.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {message.images.map((img) => (
              <a key={img.id} href={img.thumbnailUrl} target="_blank" rel="noreferrer">
                <img
                  src={img.thumbnailUrl}
                  alt="attachment"
                  loading="lazy"
                  style={{
                    maxWidth: 160,
                    maxHeight: 160,
                    borderRadius: 8,
                    border: '1px solid var(--line-2)',
                    display: 'block',
                  }}
                />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SystemMessageRow({ message }: { message: ChatMessage }) {
  return (
    <div
      role="note"
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 12px',
        border: '1px solid var(--line)',
        borderRadius: 10,
        background: 'var(--bg-2)',
        color: 'var(--text-2)',
      }}
    >
      <span style={{ color: 'var(--text-5)', marginTop: 1 }}>
        <IconInfo size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
          <span className="eyebrow">System</span>
          <RelativeTime className="mono muted" style={{ fontSize: 11 }} value={message.createdAt} />
        </div>
        <div style={{ fontSize: 12.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.content}
        </div>
      </div>
    </div>
  );
}

function PlaceholderRow({
  steps,
  waitingForOrganize,
  onCancel,
  isCancelling,
}: {
  steps: ChatMilestone[];
  waitingForOrganize: boolean;
  onCancel: () => void;
  isCancelling: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <Avatar role="asst" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Entire</div>
          <span className="chip coral working"><span className="dot" />Working on it</span>
          <button
            type="button"
            onClick={onCancel}
            disabled={isCancelling}
            className="nav-link"
            style={{ marginLeft: 'auto', height: 'auto', padding: 0, fontSize: 12, color: 'var(--accent)' }}
          >
            <IconX size={12} />
            {isCancelling ? 'Cancelling…' : 'Cancel'}
          </button>
        </div>
        {waitingForOrganize ? (
          <div className="muted" style={{ fontSize: 12, lineHeight: 1.6 }}>{t('chat.waitingForOrganize')}</div>
        ) : (
          steps.length > 0 && <StepList steps={steps} live />
        )}
      </div>
    </div>
  );
}

function LeftoverStepsRow({ steps }: { steps: ChatMilestone[] }) {
  return (
    <div style={{ paddingLeft: 38 }}>
      <StepsToggle steps={steps} />
    </div>
  );
}

function StepsToggle({ steps }: { steps: ChatMilestone[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 6 }}>
      <button
        type="button"
        className="nav-link mono"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        style={{ gap: 4, fontSize: 11.5, color: 'var(--text-5)' }}
      >
        {open ? <IconChevronD size={12} /> : <IconChevronR size={12} />}
        {t('chat.steps', { count: steps.length })}
      </button>
      {open && (
        <div
          style={{
            marginTop: 6,
            padding: '8px 10px',
            border: '1px solid var(--line)',
            borderRadius: 8,
            background: 'var(--bg-2)',
          }}
        >
          <StepList steps={steps} live={false} />
        </div>
      )}
    </div>
  );
}

function stepTime(createdAt: string): string {
  return new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function StepList({ steps, live }: { steps: ChatMilestone[]; live: boolean }) {
  const { t } = useTranslation();
  return (
    <ol
      aria-label={t('chat.stepsLabel')}
      className="mono"
      style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11.5, lineHeight: 1.5 }}
    >
      {steps.map((step, index) => {
        const current = live && index === steps.length - 1;
        return (
          <li
            key={step.id}
            aria-current={current ? 'step' : undefined}
            style={{ display: 'flex', alignItems: 'baseline', gap: 8, color: current ? 'var(--text-2)' : 'var(--text-5)' }}
          >
            <span style={{ flex: 'none', width: 10, height: '1.5em', alignSelf: 'flex-start', display: 'grid', placeItems: 'center', color: 'var(--text-6)' }}>
              {current ? (
                <span className="step-live-dot" />
              ) : (
                <IconCheck size={10} />
              )}
            </span>
            <span style={{ flex: 'none', color: 'var(--text-6)' }}>{stepTime(step.createdAt)}</span>
            <span className={current ? 'step-live-text' : undefined} style={{ minWidth: 0, wordBreak: 'break-word' }}>
              {step.text}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
